import React, { useState, useEffect, useMemo } from 'react';
import './ProductsPopup.scss';
import { closeIcon, searchIcon } from '../../utils/images';
import { ProductWithTagNames } from '../../types/Product';

interface ProductsPopupProps {
  products: ProductWithTagNames[];
  onClose: () => void;
  onGetAuthenticatedImage?: (imageUrl: string) => Promise<string>;
  onProductSelect?: (productId: number) => Promise<void>;
}

export const ProductsPopup: React.FC<ProductsPopupProps> = ({ 
  products, 
  onClose, 
  onGetAuthenticatedImage,
  onProductSelect 
}) => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [authenticatedImages, setAuthenticatedImages] = useState<{[key: string]: string}>({});
  const [loadingImages, setLoadingImages] = useState<Set<number>>(new Set());

  // Filter products based on search (memoized to prevent unnecessary recalculations)
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const query = searchQuery.toLowerCase();
      const nameMatch = product.name.toLowerCase().includes(query);
      const tagMatch = product.ai_tags.some(tag => tag.toLowerCase().includes(query));
      return nameMatch || tagMatch;
    });
  }, [products, searchQuery]);

  // Load authenticated images for all products once (not on every search)
  useEffect(() => {
    const loadAuthenticatedImages = async () => {
      if (!onGetAuthenticatedImage || products.length === 0) return;

      setLoadingImages(new Set(products.map(p => p.id)));

      const imagePromises = products.map(async (product) => {
        if (product.ai_image_url && product.ai_image_url.length > 0) {
          try {
            const authenticatedUrl = await onGetAuthenticatedImage(product.ai_image_url[0]);
            return { productId: product.id, url: authenticatedUrl };
          } catch (error) {
            console.error(`Error loading image for product ${product.id}:`, error);
            return null;
          }
        }
        return null;
      });

      const results = await Promise.all(imagePromises);
      const imageMap: {[key: string]: string} = {};
      
      results.forEach(result => {
        if (result) {
          imageMap[result.productId] = result.url;
        }
      });

      setAuthenticatedImages(imageMap);
      setLoadingImages(new Set()); // Clear loading state
    };

    loadAuthenticatedImages();
  }, [products, onGetAuthenticatedImage]); // Only depend on products, not filteredProducts

  const handleProductClick = async (productId: number) => {
    if (onProductSelect) {
      try {
        await onProductSelect(productId);
        onClose(); // Close popup after successful selection
      } catch (error) {
        console.error('Error selecting product:', error);
        // You might want to show an error message to the user here
      }
    }
  };

  return (
    <div className="products-popup">
      <div className="products-popup__header">
        <div className="products-popup__close" onClick={onClose}>
          <img className="products-popup__close--icon" src={closeIcon} alt="close" />
        </div>
        <h1 className="products-popup__title">Products Library</h1>
      </div>

      <div className="products-popup__container">
        <div className="products-popup__search">
          <img className="products-popup__search--icon" src={searchIcon} alt='search icon'/>
          <input 
            type="text" 
            className="products-popup__search--input" 
            placeholder="Search products or tags"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="products-popup__list">
          {filteredProducts.map(product => (
            <div 
              key={product.id} 
              className="products-popup__item"
              onClick={() => handleProductClick(product.id)}
            >
              <div className="products-popup__item-image">
                {loadingImages.has(product.id) ? (
                  <div className="products-popup__item-placeholder">Loading...</div>
                ) : authenticatedImages[product.id] ? (
                  <img 
                    src={authenticatedImages[product.id]} 
                    alt={product.name}
                    className="products-popup__item-img"
                  />
                ) : (
                  <div className="products-popup__item-placeholder">No image</div>
                )}
              </div>
              
              <div className="products-popup__item-content">
                <h3 className="products-popup__item-name">{product.name}</h3>
                <div className="products-popup__item-tags">
                  {product.ai_tags.map((tag, index) => (
                    <span key={index} className="products-popup__item-tag">#{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
          
          {filteredProducts.length === 0 && (
            <div className="products-popup__no-results">
              No products found matching your search.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 